import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { nameof } from '../../../utils/object';
import { DropDownPillarsStore, PillarDto } from 'stores/configuration/profiles/pillars-store';
import TailwindMultiSelect from 'widgets/editors/tailwind-multiselect';
import { useCallback, useEffect, useState } from 'react';

interface TailwindMultiplePillarsEditorProps {
  value: PillarDto[];
  onChange?: (value: PillarDto[]) => void;
  pillars?: DropDownPillarsStore;
  placeholder?: string;
  clearable?: boolean;
  className?: string;
  loadDataOnOpen?: boolean;
  disabled?: boolean;
  onBlur?: (event: any) => void;
  onFocus?: (event: any) => void;
}

const TailwindMultiplePillarsEditor: React.FC<TailwindMultiplePillarsEditorProps> = ({
  value,
  onChange,
  pillars,
  placeholder,
  clearable,
  className,
  loadDataOnOpen = false,
  disabled = false,
  onBlur,
  onFocus
}) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<PillarDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getOptionsMethod = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await pillars.getAllAsync({
        searchQuery: '',
        skip: 0,
        take: 100000,
        orderBy: [{ direction: 'Ascending', field: nameof<PillarDto>('name'), useProfile: false }],
        filter: []
      });

      if (result) {
        setOptions(result.items || []);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [pillars]);

  const handleChange = (newValue: PillarDto[]) => {
    if (onChange) {
      const selectedPllars = newValue.map(v => options.find(opt => opt.id === v.id)).filter(Boolean);
      onChange(selectedPllars);
    }
  };

  const onOpenDropDown = useCallback(() => {
    if (loadDataOnOpen && options.length === 0) {
      getOptionsMethod();
    }
  }, [loadDataOnOpen, options.length, getOptionsMethod]);

  useEffect(() => {
    if (!loadDataOnOpen) {
      getOptionsMethod();
    }
  }, [loadDataOnOpen, getOptionsMethod]);

  return (
    <div className="relative w-full">
      <TailwindMultiSelect
        options={
          isLoading
            ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
            : options.length > 0
            ? options.map(pillar => ({
                id: pillar.id,
                text: pillar.name
              }))
            : [{ id: 'no-options', text: t('No options available'), disabled: true }]
        }
        value={value?.map(v => ({
          id: v.id,
          text: v.name
        }))}
        onChange={handleChange}
        placeholder={placeholder || t('Select location')}
        clearable={clearable}
        className={`w-full ${className}`}
        onBlur={onBlur}
        onFocus={onFocus}
        searchable={!isLoading}
        disabled={disabled}
        onOpenDropDown={onOpenDropDown}
      />
    </div>
  );
};

// Wire up the React component to the Redux store
export default connect(['pillars', DropDownPillarsStore])(TailwindMultiplePillarsEditor);
